import React, { useContext, useState } from 'react';
import { BrandContext } from '@/contexts/BrandContext';
import { ChevronDown, Check, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function BrandSwitcher() {
  const { brands, activeBrand, switchBrand, loading } = useContext(BrandContext);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="px-3 py-2">
        <div className="animate-pulse bg-gray-800 h-10 rounded-lg"></div>
      </div>
    );
  }

  if (!activeBrand && brands.length === 0) {
    return (
      <div className="px-3 py-2">
        <button
          onClick={() => navigate('/brands/new')}
          className="w-full px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Brand
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 relative">
      {/* Active Brand Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {activeBrand?.logo_url ? (
            <img
              src={activeBrand.logo_url}
              alt={activeBrand.name}
              className="w-6 h-6 object-contain rounded"
            />
          ) : (
            <div
              className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: activeBrand?.primary_color || '#06b6d4' }}
            >
              {activeBrand?.name?.charAt(0) || 'B'}
            </div>
          )}
          <span className="text-white text-sm font-medium truncate">
            {activeBrand?.name || 'No Brand'}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute left-3 right-3 mt-2 bg-[#1a1d23] border border-gray-800 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
            {/* Brand List */}
            <div className="py-1">
              {brands.map(brand => (
                <button
                  key={brand.id}
                  onClick={() => {
                    switchBrand(brand);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-800 transition-colors ${
                    activeBrand?.id === brand.id ? 'bg-gray-800' : ''
                  }`}
                >
                  {brand.logo_url ? (
                    <img
                      src={brand.logo_url}
                      alt={brand.name}
                      className="w-6 h-6 object-contain rounded"
                    />
                  ) : (
                    <div
                      className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: brand.primary_color }}
                    >
                      {brand.name.charAt(0)}
                    </div>
                  )}
                  <span className="text-white text-sm flex-1">{brand.name}</span>
                  {activeBrand?.id === brand.id && (
                    <Check className="w-4 h-4 text-cyan-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-800" />

            {/* Create New Brand */}
            <button
              onClick={() => {
                navigate('/brands/new');
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-800 transition-colors text-cyan-500"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">Create New Brand</span>
            </button>

            {/* Manage Brands */}
            <button
              onClick={() => {
                navigate('/brands');
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-800 transition-colors text-gray-400"
            >
              <span className="text-sm">Manage All Brands</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
